import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SUPPORT_EMAIL, COMPANY_NAME, DBA_NAME, WEBSITE_URL } from '../constants/contact'

export default function Refund() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="text-xl font-bold text-paybyphone-green hover:text-paybyphone-green/80 transition-colors">
            {DBA_NAME}
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Refund Policy</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective Date:</strong> May 19, 2026<br />
              <strong>Last Updated:</strong> May 19, 2026
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Overview</h2>
              <p className="text-gray-700 mb-4">
                This Refund Policy describes the terms under which {COMPANY_NAME}, doing business as {DBA_NAME}
                (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;), issues refunds for paid subscriptions and one-time purchases made
                through {WEBSITE_URL} (the &quot;Service&quot;). By subscribing to or purchasing the Service, you agree
                to this Refund Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Subscription Plans</h2>
              <p className="text-gray-700 mb-4">
                The Service is offered on a recurring subscription basis (weekly or monthly), as well as a
                pay-as-you-go option for time-limited parking sessions. Subscription fees are charged at the
                start of each billing cycle in U.S. dollars (USD) and are non-refundable except as expressly
                described in this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Cancellation</h2>
              <p className="text-gray-700 mb-4">
                You may cancel your subscription at any time from your account settings. Upon cancellation:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Your subscription will remain active until the end of the current billing period.</li>
                <li>You will not be charged for any subsequent billing periods.</li>
                <li>No partial refund will be issued for the unused portion of the current billing period.</li>
                <li>Pay-as-you-go sessions are non-refundable once a payment has been authorized.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Eligibility for Refunds</h2>
              <p className="text-gray-700 mb-4">
                Refunds may be granted, at our sole discretion, in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>
                  <strong>Duplicate charge:</strong> If you were charged more than once for the same billing period
                  due to a technical error.
                </li>
                <li>
                  <strong>Unauthorized charge:</strong> If a charge was made to your payment method without your
                  authorization (subject to verification).
                </li>
                <li>
                  <strong>Service unavailable:</strong> If the Service was materially unavailable for a substantial
                  portion of a billing period due to a fault on our side (not including third-party outages, planned
                  maintenance, or force majeure).
                </li>
              </ul>
              <p className="text-gray-700 mb-4">
                Refunds are <strong>not</strong> issued for:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Lack of use of the Service during a paid period.</li>
                <li>Change of mind after a successful charge.</li>
                <li>Parking fees, tickets, fines, or penalties incurred by the user, which remain the sole
                  responsibility of the user.</li>
                <li>Inability to find a parking spot in a specific location.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. How to Request a Refund</h2>
              <p className="text-gray-700 mb-4">
                To request a refund, send an email to <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green underline">{SUPPORT_EMAIL}</a> within
                <strong> 30 days</strong> of the disputed charge. Please include:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>The email address associated with your account.</li>
                <li>The date and amount of the charge.</li>
                <li>A clear description of the reason for the refund request.</li>
                <li>Any supporting documentation (screenshots, transaction IDs).</li>
              </ul>
              <p className="text-gray-700 mb-4">
                We will review your request and respond within <strong>5 business days</strong>. If approved, the
                refund will be issued to the original payment method used for the purchase. Refunds typically
                appear on your statement within <strong>5–10 business days</strong>, depending on your card issuer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Chargebacks</h2>
              <p className="text-gray-700 mb-4">
                We strongly encourage you to contact our customer support team at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green underline">{SUPPORT_EMAIL}</a> before
                initiating a chargeback with your card issuer. Initiating a chargeback without first contacting us
                may result in suspension or termination of your account and may forfeit your right to a refund.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify this Refund Policy at any time. Material changes will be posted on
                this page with an updated &quot;Last Updated&quot; date. Continued use of the Service after such changes
                constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Refund Policy, please contact us:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Email:</strong> <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green underline">{SUPPORT_EMAIL}</a></li>
                <li><strong>Company:</strong> {COMPANY_NAME} (DBA {DBA_NAME})</li>
                <li><strong>Website:</strong> {WEBSITE_URL}</li>
              </ul>
              <p className="text-gray-500 text-sm mt-4">
                See also our <Link to="/contact" className="text-paybyphone-green underline">Contact page</Link> for
                phone and mailing address.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
